import { getApiClient } from './authApiClient';

// Keep every request bound to the selected workspace. The server independently
// verifies tenant membership; neither model JSON nor request options choose it.
export const createRiskModelApi = (tenantId, { readOnly = false } = {}) => {
  if (typeof tenantId !== 'string' || !tenantId.trim()) {
    throw new Error('A workspace is required to open Model Studio.');
  }

  const client = getApiClient('company');
  const base = `/risk-models/tenants/${encodeURIComponent(tenantId)}`;

  const request = async (method, path, payload) => {
    if (readOnly && method !== 'get') {
      const error = new Error('Model Studio is read-only for this account or support preview.');
      error.status = 403;
      throw error;
    }

    try {
      const response = await client[method](`${base}${path}`, ...(payload === undefined ? [] : [payload]));
      if (response.data?.success === false) {
        const error = new Error(response.data.message || 'The Model Studio request failed.');
        error.status = response.status;
        error.response = response;
        throw error;
      }
      return response.data?.data;
    } catch (error) {
      const detail = error.response?.data;
      const message = detail?.message || (typeof detail?.error === 'string' ? detail.error : null);
      if (message) error.message = message;
      error.status = error.response?.status || error.status;
      throw error;
    }
  };

  return {
    loadWorkspace: () => request('get', '/workspace'),
    saveDraft: (model, expectedRevision) => request('put', '/draft', { expectedRevision, model }),
    // Saving a version records a snapshot. It does not activate production policy.
    createVersion: (expectedRevision) => request('post', '/versions', { expectedRevision }),
    runBacktest: (model, { companyIds, baselineModel, extractionId } = {}) => request('post', '/backtests', {
      model,
      ...(companyIds !== undefined ? { companyIds } : {}),
      ...(baselineModel !== undefined ? { baselineModel } : {}),
      ...(extractionId !== undefined ? { extractionId } : {}),
    }),
    extractFields: (model, { companyIds } = {}) => request('post', '/extractions', {
      model,
      ...(companyIds !== undefined ? { companyIds } : {}),
    }),
    reviewModel: (model, { backtestId } = {}) => request('post', '/reviews', {
      model,
      ...(backtestId !== undefined ? { backtestId } : {}),
    }),
  };
};
